import { useMemo } from 'react';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { validateDryRunRoute, validateMainRoute } from '@lp/features/RoutePlanner/utils';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { Case, CaseStatus, DonorType } from '@/api/apis/case';
import { isAllowedStatus } from '@/utils/helpers';
import { LogisticsActionsMenu } from './LogisticsActionsMenu';
import { RouteLogisticsButtons } from './RouteLogisticsButtons';
import { RouteTypeEnum } from '@/api/apis/route';

type RouteStatusMenuProps = {
  isValidLocations: boolean;
};

export const RouteStatusMenu = ({ isValidLocations }: RouteStatusMenuProps) => {
  const { legs: mainLegs } = useRouteDetails(RouteTypeEnum.Main);
  const { legs: dryRunLegs } = useRouteDetails(RouteTypeEnum.DryRun);
  const {
    status,
    donorType,
    opoRequest,
    isExpeditedCase,
    reAllocationCaseId,
    txCenterDetails,
    donorHospitalDetails,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    randomization,
  } = useCaseDetailsWithReviews() as Case;

  const isActiveCaseStatus = useMemo(() => {
    return isAllowedStatus({
      statusFrom: CaseStatus.PendingLogistics,
      statusTo: CaseStatus.ReturningToBase,
      currentStatus: status,
    });
  }, [status]);

  const routeValidation = useMemo<{ isValid: boolean; errorMessage: string }>(() => {
    if (!donorHospitalDate || !donorHospitalTime || !donorHospitalTimeZone) {
      return {
        isValid: false,
        errorMessage: 'Missing Donor OR time',
      };
    }

    const opoRequestWithoutTx = opoRequest && !txCenterDetails;
    const expeditedCaseWithoutTx = !!isExpeditedCase && !txCenterDetails;
    const primary = { donorHospitalId: donorHospitalDetails?.id, txCenterId: txCenterDetails?.id };

    const mainRouteValidation = validateMainRoute({
      legs: mainLegs,
      opoRequest: opoRequestWithoutTx,
      expeditedCase: expeditedCaseWithoutTx,
      reAllocationCase: !!reAllocationCaseId,
      randomization,
      primary,
    });

    if (!mainRouteValidation.isValid) {
      return mainRouteValidation;
    }

    if (donorType === DonorType.DCD && !reAllocationCaseId) {
      const dryRunValidation = validateDryRunRoute(dryRunLegs, primary);

      if (!dryRunValidation.isValid) {
        return dryRunValidation;
      }
    }

    return {
      isValid: true,
      errorMessage: '',
    };
  }, [
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    opoRequest,
    txCenterDetails,
    donorHospitalDetails,
    isExpeditedCase,
    mainLegs,
    reAllocationCaseId,
    randomization,
    donorType,
    dryRunLegs,
  ]);

  return (
    <div className="flex items-center gap-3 ml-auto mr-3">
      {isActiveCaseStatus && (
        <RouteLogisticsButtons
          isValidRoute={routeValidation.isValid}
          errorMessage={routeValidation.errorMessage}
          isValidLocations={isValidLocations}
        />
      )}
      <LogisticsActionsMenu isRouteValid={routeValidation.isValid} />
    </div>
  );
};
